import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';

import { InvoiceDetails } from 'src/app/shared/models/invoice-details';
import { BillingService } from 'src/app/core/services/billing.service';
import { GooleAnalyticsEventsService } from 'src/app/core/services/goole-analytics-events.service';
import { MatDialog } from '@angular/material/dialog';
import { StripeDialogComponent } from './stripe-dialog/stripe-dialog.component';
import { saveAs } from 'file-saver';
import { RefreshService } from 'src/app/core/services/refresh.service';
import { RequestStoreService } from 'src/app/core/services/request-store.service';
import { RequestModel } from 'src/app/core/models/request.model';
import { RequestType } from 'src/app/core/enums/request-type.enum';
import { AccountService } from 'src/app/core/services/account.service';
import { DirectDebitComponent } from './direct-debit/direct-debit-dialog.component';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { NgClass, KeyValuePipe } from '@angular/common';
import { MatList, MatListItem } from '@angular/material/list';
import { MatCard } from '@angular/material/card';
import { PageHeaderComponent } from '../../../../../shared/components/layout/page-header/page-header.component';

@Component({
    selector: 'app-invoice-details',
    templateUrl: './invoice-details.component.html',
    styleUrls: ['./invoice-details.component.scss'],
    standalone: true,
    imports: [PageHeaderComponent, MatCard, MatList, MatListItem, NgClass, ExtendedModule, MatProgressSpinner, MatButton, KeyValuePipe]
})
export class InvoiceDetailsComponent implements OnInit, OnDestroy {

  private subscription: Subscription;
  invoiceDetails: { [key: string]: InvoiceDetails[] };
  hasData = false;
  headerImage: string;
  invoiceDetailsState = '';
  invoiceDate = '';
  invoiceTotal = '';
  invoiceVatTotal = '';
  invoiceNumber = '';
  invoiceOutstanding = '';
  invoiceState = '';
  invoicePaid = true;
  intentSecret: string;
  invoiceId: string;
  stripePublicKey = '';
  directDebit = '';
  loadingStripe = false;
  hideButton = true;
  paymentSuccess = false;
  downloadLoading = false;
  payLoading = false;
  fileToDownload: any;
  directDebitEnabled: boolean;
  gocardlessUrl: string;
  customerId: string;
  fetchingDirectDebitLink = false;
  directDebitLink = '';
  directDebitPaymentScheduled = false;

  appLoaderAnimation = './../../../../../../assets/images/dogloading.gif';

  // tslint:disable-next-line: max-line-length
  constructor(private accountService: AccountService, private route: ActivatedRoute, private billingService: BillingService, breakpointObserver: BreakpointObserver, private googleAnalyticsEvents: GooleAnalyticsEventsService, private dialog: MatDialog, private requestService: RequestStoreService,) {
    breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.TabletPortrait
    ]).subscribe(result => {
      if (result.matches) {
        this.headerImage = './../../../../../../assets/images/backgrounds/billing/billing_header_mobile.jpg';
      } else {
        this.headerImage = './../../../../../../assets/images/backgrounds/billing/billing_header_desktop.jpg';
      }
    });
  }

  ngOnInit() {
    this.invoiceId = this.route.snapshot.paramMap.get('id');
    this.fetchInvoicesRows(this.invoiceId);
  }

  getDownloadFile() {
    this.downloadLoading = true;
    this.billingService.downloadInvoice(this.invoiceId).subscribe(result => {
      const byteString = window.atob(result['data']);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([int8Array], { type: 'application/pdf' });
      this.downloadLoading = false;
      this.googleEvent(this.invoiceNumber, 'download');
      saveAs(blob, `${this.invoiceNumber}.pdf`);
    }, err => {
      console.error('DOWNLOAD INVOICE ERROR! ', err);
    });
  }

  loadStripe(id) {
    this.loadingStripe = true;
    this.billingService.createPaymentIntent(id).subscribe((result) => {
      this.intentSecret = result['intent_secret'];
      this.stripePublicKey = result['stripe_public_key'];

      if (result['stripe_presence']) {
        this.hideButton = true;
      } else {
        this.hideButton = false;
      }

      this.loadingStripe = false;

    }, err => {
      console.error('DID NOT FETCH PAYMENT INTENT ', err);
    });

  }

  payInvoice() {
    this.loadingStripe = true;
    this.fetchAccountInfo();
  }

  proceedWithStripe() {
    if (this.stripePublicKey === '') {
      this.billingService.createPaymentIntent(this.invoiceId).subscribe(result => {
        this.intentSecret = result['intent_secret'];
        this.stripePublicKey = result['stripe_public_key'];
        this.loadingStripe = false;

        const dialog = this.dialog.open(StripeDialogComponent, {
          data: {
            intentSecret: this.intentSecret,
            stripePublicKey: this.stripePublicKey,
            invoiceTotal: this.invoiceTotal,
            invoiceId: this.invoiceId
          },
          panelClass: 'stripe-dialog-container',
          maxWidth: '90vw',
          autoFocus: false
        });

        dialog.afterClosed().subscribe(stripeResponse => {
          if (stripeResponse['event'] === 'close') {
            this.hideButton = false;
          } else if (stripeResponse['data'] !== '') {
            this.hideButton = true;
            this.invoicePaid = true;
          }
        });
      }, err => {
        console.error('DID NOT FETCH PAYMENT INTENT ', err);
      });
    } else {
      this.loadingStripe = false;

      const dialog = this.dialog.open(StripeDialogComponent, {
        data: {
          intentSecret: this.intentSecret,
          stripePublicKey: this.stripePublicKey,
          invoiceTotal: this.invoiceTotal,
          invoiceId: this.invoiceId
        },
        panelClass: 'stripe-dialog-container',
        maxWidth: '90vw',
        autoFocus: false
      });

      dialog.afterClosed().subscribe(stripeResponse => {
        const response = stripeResponse || { event: "close" };
        if (response.event === "close") {
          this.hideButton = false;
        } else if (response.data !== "") {
          this.hideButton = true;
          this.invoicePaid = true;
        }
      });
    }
  }

  fetchAccountInfo() {
    this.subscription = this.accountService.getDirectDebitStatus().subscribe(result => {
      const response = result['response'];
      this.directDebitEnabled = response['enabled'];
      this.gocardlessUrl = response['direct_debit_link'];
      this.customerId = response['customer_id'];
      this.directDebitLink = response['direct_debit_link'];
      this.hasData = true;

      if (this.directDebitEnabled) {
        this.proceedWithStripe();
      } else {
        const dialog = this.dialog.open(DirectDebitComponent, {
          data: {
            gocardlessUrl: this.gocardlessUrl,
            invoiceId: this.invoiceId
          }
        });

        dialog.afterClosed().subscribe(response => {
          if (response === 'no_thanks') {
            this.proceedWithStripe();
          } else if (response === 'setup_direct_debit') {
            window.open(this.gocardlessUrl, '_self');
            this.googleEvent('direct_debit', 'setup');
          }
          this.loadingStripe = false;
        });
      };
    }, err => {
      console.error('GET DIRECT DEBIT STATUS ERROR! ', err);
    });
  }

  fetchInvoicesRows(id) {
    this.invoiceId = id;
    this.requestService.addRequest(new RequestModel(
      '/customer_object/get_dog_specific_invoice_rows?id=' + id,
      RequestType.GET,
    ));
    this.requestService.dispatch().subscribe((resp) => {
      this.invoiceState = resp[0]['state'];
      this.invoiceDetails = resp[0]['invoiceRows'];
      this.directDebit = resp[0]['isDirectDebit'];
      this.invoiceTotal = resp[0]['total'];
      this.invoiceVatTotal = resp[0]['totalVat'];
      this.invoiceDate = resp[0]['invoiceDate'];
      this.invoiceNumber = resp[0]['invoiceNumber'];
      this.invoiceOutstanding = resp[0]['outstanding'];
      this.invoicePaid = resp[0]['invoicePaid'];
      if (this.invoiceDetails !== undefined) {
        this.hasData = true;
        this.hideButton = this.invoicePaid;
        if (!this.invoicePaid) {
          this.loadStripe(this.invoiceId);
        }
      } else {
        this.invoiceDetailsState = 'No invoices items available';
      }
      if (resp[0]['go_cardless_status'] == 'Payment scheduled') {
        this.directDebitPaymentScheduled = true
      }

    }, err => {
      console.error('INVOICE ROWS FETCH ERROR! ', err);
    });
  }

  getDisplayName(name: string): string {
    return name !== 'misc' ? name : 'Miscellaneous';
  }

  hasDogSpecificRows(obj) {
    const keys = Object.keys(obj);
    return keys.length > 1;
  }

  ngOnDestroy() {
  }

  googleEvent(section, cardTitle) {
    this
      .googleAnalyticsEvents
      .eventEmitter(cardTitle, 'invoice details', 'invoice details', 10);
  }

}
